/**
 * Channel Orchestrator Service
 * Phase 4 Compliant - Implements IService
 *
 * Manages notification delivery across multiple channels with:
 * - Priority-based delivery
 * - Fallback strategies
 * - Retry logic with exponential backoff
 * - Channel health monitoring
 */
import { IService, ServiceResponse, createServiceError} from "../service-registry";
import type { Notification } from "../../types/notification";
import { logger } from "../../lib/logger";
// ============================================================================
// TYPES & INTERFACES
// ============================================================================
export type NotificationChannel = 'database' | 'push' | 'email' | 'discord' | 'whatsapp' | 'telegram';
export interface ChannelConfig {
    name: NotificationChannel;
    priority: number;
    enabled: boolean;
    fallbackTo?: NotificationChannel;
    timeout: number;
    retryAttempts: number;
    retryDelay: number; // Base delay in ms for exponential backoff
}
export interface ChannelResult {
    channel: NotificationChannel;
    success: boolean;
    timestamp: Date;
    duration: number;
    error?: string;
    fallbackUsed?: boolean;
}
export interface MultiChannelResult {
    notification_id: string;
    channels: ChannelResult[];
    allSuccessful: boolean;
    primarySuccessful: boolean;
    totalDuration: number;
}
export interface IChannelOrchestrator extends IService {
    deliverToAllChannels(notification: Notification): Promise<ServiceResponse<MultiChannelResult>>;
    deliverToChannel(channel: NotificationChannel, notification: Notification): Promise<ServiceResponse<ChannelResult>>;
    updateChannelConfig(channel: NotificationChannel, config: Partial<ChannelConfig>): Promise<ServiceResponse<void>>;
    getChannelHealth(): Promise<ServiceResponse<Record<NotificationChannel, ChannelHealthStatus>>>;
}
export interface ChannelHealthStatus {
    available: boolean;
    lastSuccessAt?: Date;
    lastFailureAt?: Date;
    successRate: number; // 0-1
    avgResponseTime: number; // ms
}
// ============================================================================
// CHANNEL ORCHESTRATOR SERVICE
// ============================================================================
export class ChannelOrchestrator implements IChannelOrchestrator {
    readonly name = "channelOrchestrator" as const;
    private channelConfigs: Map<NotificationChannel, ChannelConfig> = new Map();
    private channelHealth: Map<NotificationChannel, ChannelHealthStatus> = new Map();
    private deliveryMetrics: Map<NotificationChannel, ChannelResult[]> = new Map();
    constructor() {
        this.initializeChannelConfigs();
        this.initializeChannelHealth();
    }
    async initialize(): Promise<void> {
        logger.info('🎯 ChannelOrchestrator initialized', { channelCount: this.channelConfigs.size });
    }
    async dispose(): Promise<void> {
        this.channelConfigs.clear();
        this.channelHealth.clear();
        this.deliveryMetrics.clear();
        logger.info('🎯 ChannelOrchestrator disposed');
    }
    // ========================================================================
    // CHANNEL CONFIGURATION
    // ========================================================================
    private initializeChannelConfigs(): void {
        const defaultConfigs: ChannelConfig[] = [
            {
                name: 'database',
                priority: 1,
                enabled: true,
                timeout: 3000,
                retryAttempts: 3,
                retryDelay: 1000,
            },
            {
                name: 'push',
                priority: 2,
                enabled: true,
                fallbackTo: 'email',
                timeout: 5000,
                retryAttempts: 2,
                retryDelay: 2000,
            },
            {
                name: 'email',
                priority: 3,
                enabled: true,
                timeout: 10000,
                retryAttempts: 3,
                retryDelay: 3000,
            },
            {
                name: 'discord',
                priority: 4,
                enabled: true,
                timeout: 5000,
                retryAttempts: 2,
                retryDelay: 2000,
            },
            {
                name: 'whatsapp',
                priority: 5,
                enabled: true,
                fallbackTo: 'email',
                timeout: 10000,
                retryAttempts: 2,
                retryDelay: 3000,
            },
            {
                name: 'telegram',
                priority: 6,
                enabled: true,
                timeout: 5000,
                retryAttempts: 2,
                retryDelay: 2000,
            },
        ];
        defaultConfigs.forEach(config => {
            this.channelConfigs.set(config.name, config);
        });
    }
    private initializeChannelHealth(): void {
        const channels: NotificationChannel[] = ['database', 'push', 'email', 'discord', 'whatsapp', 'telegram'];
        channels.forEach(channel => {
            this.channelHealth.set(channel, {
                available: true,
                successRate: 1.0,
                avgResponseTime: 0,
            });
        });
    }
    async updateChannelConfig(channel: NotificationChannel, config: Partial<ChannelConfig>): Promise<ServiceResponse<void>> {
        try {
            const currentConfig = this.channelConfigs.get(channel);
            if (!currentConfig) {
                return { success: false, error: `Channel ${channel} not found`, code: 'CHANNEL_NOT_FOUND', service: this.name };
            }
            this.channelConfigs.set(channel, { ...currentConfig, ...config });
            logger.info('📝 Updated channel config', { channel });
            return { success: true, data: undefined };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'CONFIG_UPDATE_ERROR', service: this.name };
        }
    }
    // ========================================================================
    // CHANNEL DELIVERY
    // ========================================================================
    async deliverToAllChannels(notification: Notification): Promise<ServiceResponse<MultiChannelResult>> {
        const startTime = performance.now();
        const results: ChannelResult[] = [];
        try {
            // Get enabled channels sorted by priority
            const sortedChannels = Array.from(this.channelConfigs.values())
                .filter(config => config.enabled)
                .sort((a, b) => a.priority - b.priority);
            logger.info('🎯 Delivering notification', { notificationId: notification.id, channelCount: sortedChannels.length });
            // Deliver to all channels in parallel (within priority groups)
            const deliveryPromises = sortedChannels.map(async (config) => {
                const channelResult = await this.deliverToChannel(config.name, notification);
                return channelResult.data!;
            });
            const channelResults = await Promise.all(deliveryPromises);
            results.push(...channelResults);
            const allSuccessful = results.every(r => r.success);
            const primarySuccessful = results.find(r => r.channel === 'database')?.success || false;
            const totalDuration = performance.now() - startTime;
            const result: MultiChannelResult = {
                notification_id: notification.id,
                channels: results,
                allSuccessful,
                primarySuccessful,
                totalDuration,
            };
            logger.info('✅ Multi-channel delivery complete', { notificationId: notification.id,
                allSuccessful,
                primarySuccessful,
                totalDuration: `${totalDuration.toFixed(2)}ms`,
            });
            return { success: true, data: result };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'MULTI_CHANNEL_DELIVERY_ERROR', service: this.name };
        }
    }
    async deliverToChannel(channel: NotificationChannel, notification: Notification): Promise<ServiceResponse<ChannelResult>> {
        const config = this.channelConfigs.get(channel);
        if (!config || !config.enabled) {
            return { success: true, data: {
                    channel,
                    success: false,
                    timestamp: new Date(),
                    duration: 0,
                    error: 'Channel not enabled',
                } };
        }
        // Attempt delivery with retry logic
        let lastError: string | undefined;
        for (let attempt = 0; attempt <= config.retryAttempts; attempt++) {
            const startTime = performance.now();
            try {
                const deliveryResult = await this.executeChannelDelivery(channel, notification, config.timeout);
                const duration = performance.now() - startTime;
                if (deliveryResult.success) {
                    const result: ChannelResult = {
                        channel,
                        success: true,
                        timestamp: new Date(),
                        duration,
                    };
                    // Update health metrics
                    this.updateChannelHealth(channel, true, duration);
                    this.recordDeliveryMetric(channel, result);
                    return { success: true, data: result };
                }
                lastError = deliveryResult.error;
            }
            catch (error) {
                lastError = error instanceof Error ? error.message : 'Unknown error';
            }
            // Wait before retry (exponential backoff)
            if (attempt < config.retryAttempts) {
                const delay = config.retryDelay * Math.pow(2, attempt);
                logger.debug('⏳ Retrying delivery', { channel, delay, attempt: attempt + 1, maxAttempts: config.retryAttempts });
                await this.sleep(delay);
            }
        }
        // All retries failed, try fallback if available
        if (config.fallbackTo) {
            logger.info('🔄 Attempting fallback', { from: channel, to: config.fallbackTo });
            const fallbackResult = await this.deliverToChannel(config.fallbackTo, notification);
            if (fallbackResult.data?.success) {
                return { success: true, data: {
                        ...fallbackResult.data,
                        fallbackUsed: true,
                    } };
            }
        }
        // Complete failure
        const duration = performance.now();
        const result: ChannelResult = {
            channel,
            success: false,
            timestamp: new Date(),
            duration,
            error: lastError || 'Delivery failed',
        };
        this.updateChannelHealth(channel, false, duration);
        this.recordDeliveryMetric(channel, result);
        return { success: true, data: result };
    }
    private async executeChannelDelivery(channel: NotificationChannel, notification: Notification, timeout: number): Promise<{
        success: boolean;
        error?: string;
    }> {
        // Create timeout promise
        const timeoutPromise = new Promise<{
            success: false;
            error: string;
        }>((resolve) => {
            setTimeout(() => {
                resolve({ success: false, error: 'Delivery timeout' });
            }, timeout);
        });
        // Create delivery promise
        const deliveryPromise = this.deliverViaChannel(channel, notification);
        // Race between delivery and timeout
        return Promise.race([deliveryPromise, timeoutPromise]);
    }
    private async deliverViaChannel(channel: NotificationChannel, notification: Notification): Promise<{
        success: boolean;
        error?: string;
    }> {
        try {
            switch (channel) {
                case 'database':
                    // Database delivery is already handled by notification creation
                    return { success: true };
                case 'push':
                    // Push notifications are delivered client-side (browser) based on realtime inserts.
                    logger.debug('📱 Push notification queued', { notificationId: notification.id });
                    return { success: true };
                case 'email': {
                    // Email delivery via Resend
                    const emailData = notification.data as Record<string, unknown> | null;
                    if (emailData?.needs_email) {
                        // await emailService.send(notification);
                        logger.info('📧 Email notification sent', { notificationId: notification.id });
                    }
                    return { success: true };
                }
                case 'discord': {
                    // Discord delivery is handled at the application layer (where user preferences & webhook live).
                    logger.debug('💬 Discord notification queued', { notificationId: notification.id });
                    return { success: true };
                }
                case 'whatsapp': {
                    // WhatsApp delivery via n8n/Twilio
                    const whatsappData = notification.data as Record<string, unknown> | null;
                    if (whatsappData?.whatsapp_number) {
                        // await whatsappService.send(notification);
                        logger.info('📲 WhatsApp notification sent', { notificationId: notification.id });
                    }
                    return { success: true };
                }
                case 'telegram': {
                    // Telegram delivery via Bot API.
                    //
                    // IMPORTANT: This code path is server-side only (Node.js services, edge functions).
                    // The Telegram bot token must NEVER be exposed to browser bundles.
                    // Set TELEGRAM_BOT_TOKEN as a server-side environment variable (no VITE_ prefix).
                    //
                    // Prerequisites:
                    //   - TELEGRAM_BOT_TOKEN: bot token from @BotFather
                    //   - notification.data.telegram_chat_id: user's Telegram chat_id (obtained when
                    //     the user sends /start to the bot, stored in notification_preferences.telegram_chat_id)
                    const telegramData = notification.data as Record<string, unknown> | null;
                    const chatId = telegramData?.telegram_chat_id as string | undefined;
                    if (!chatId) {
                        // No linked Telegram account — not an error, just a no-op
                        return { success: true };
                    }
                    // Server-side only: process.env is intentionally not prefixed with VITE_
                    const botToken = typeof process !== 'undefined'
                        ? process.env.TELEGRAM_BOT_TOKEN ?? ''
                        : '';
                    if (!botToken) {
                        logger.warn('⚠️ TELEGRAM_BOT_TOKEN not set — Telegram delivery skipped', {
                            notificationId: notification.id,
                        });
                        return { success: false, error: 'TELEGRAM_BOT_TOKEN not configured' };
                    }
                    const message = notification.message_preview ?? 'You have a new notification.';
                    const url = `https://api.telegram.org/bot${botToken}/sendMessage`;
                    const response = await fetch(url, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            chat_id: chatId,
                            text: message,
                            parse_mode: 'HTML',
                        }),
                    });
                    if (!response.ok) {
                        const errorText = await response.text();
                        logger.error('❌ Telegram delivery failed', {
                            notificationId: notification.id,
                            status: response.status,
                            error: errorText,
                        });
                        return { success: false, error: `Telegram API error ${response.status}` };
                    }
                    logger.info('✈️ Telegram notification sent', { notificationId: notification.id, chatId });
                    return { success: true };
                }
                default:
                    return { success: false, error: `Unknown channel: ${channel}` };
            }
        }
        catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Channel delivery error',
            };
        }
    }
    // ========================================================================
    // HEALTH MONITORING
    // ========================================================================
    private updateChannelHealth(channel: NotificationChannel, success: boolean, duration: number): void {
        const health = this.channelHealth.get(channel);
        if (!health)
            return;
        // Update success/failure timestamps
        if (success) {
            health.lastSuccessAt = new Date();
        }
        else {
            health.lastFailureAt = new Date();
        }
        // Calculate rolling success rate (last 100 deliveries)
        const metrics = this.deliveryMetrics.get(channel) || [];
        const recentMetrics = metrics.slice(-100);
        const successCount = recentMetrics.filter(m => m.success).length;
        health.successRate = recentMetrics.length > 0 ? successCount / recentMetrics.length : 1.0;
        // Calculate average response time
        const successfulMetrics = recentMetrics.filter(m => m.success);
        health.avgResponseTime = successfulMetrics.length > 0
            ? successfulMetrics.reduce((sum, m) => sum + m.duration, 0) / successfulMetrics.length
            : 0;
        // Mark as unavailable if success rate drops below 50%
        health.available = health.successRate >= 0.5;
        this.channelHealth.set(channel, health);
    }
    private recordDeliveryMetric(channel: NotificationChannel, result: ChannelResult): void {
        if (!this.deliveryMetrics.has(channel)) {
            this.deliveryMetrics.set(channel, []);
        }
        const metrics = this.deliveryMetrics.get(channel)!;
        metrics.push(result);
        // Keep only last 1000 metrics per channel
        if (metrics.length > 1000) {
            metrics.shift();
        }
    }
    async getChannelHealth(): Promise<ServiceResponse<Record<NotificationChannel, ChannelHealthStatus>>> {
        try {
            const channels: NotificationChannel[] = ['database', 'push', 'email', 'discord', 'whatsapp', 'telegram'];
            const healthStatus: Record<NotificationChannel, ChannelHealthStatus> = Object.fromEntries(channels.map((channel) => {
                const health = this.channelHealth.get(channel);
                return [
                    channel,
                    health
                        ? { ...health }
                        : {
                            available: false,
                            successRate: 0,
                            avgResponseTime: 0,
                        },
                ];
            })) as Record<NotificationChannel, ChannelHealthStatus>;
            return { success: true, data: healthStatus };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'HEALTH_CHECK_ERROR', service: this.name };
        }
    }
    // ========================================================================
    // UTILITIES
    // ========================================================================
    private sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
export const channelOrchestrator = new ChannelOrchestrator();
